import { useState, useEffect, useRef } from 'react'
import TrailingReturnsGraph from './TRGraph'
import { useQuery } from '@tanstack/react-query'
import { useMetaDataContext } from '../../../state-management/contexts/MetaDataContext'
import { fetchTrailingReturnsPerformanceData } from '../../../repository/MutualFundDetail'
import { TrailingReturnsPerformanceData } from '../../../types/mutual-fund-detail.types'
import ToggleBox from '../../ui-components/ToggleBox'
import { convertUnitAndValue, formatDate, roundOffAndFormatNumberWithCurrency } from '../../../utils/utils'
import { EventNames, TIME_UNITS, UNITS } from '../../../types/misc.types'
import Tabs from '../../ui-components/Tabs'
import TrailingGraphLoader from '../../skeletons/TrailingGraphLoader'
import TextBubble from '../../ui-components/TextBubble'
import GraphUnavailable from '../../error-states/GraphUnavailable'
import Remark from '../../ui-components/Remark'
import { lightImpactVibrate, trackEvent } from '../../../utils/appBridge'
import HelpButton from '../../ui-components/HelpButton'
import { ReturnsBottomSheet } from '../../BottomSheetComponents/ReturnsBottomSheet'
import VaulDrawer from '../../BottomSheetComponents/VaulDrawer'
import { sortTenures } from '../../../utils/mutualFundDetail'
import { TRLearnMoreBottomSheet } from '../../BottomSheetComponents/TRLearnMoreBottomSheet'

type FundReturnType = {
  amount: string
  returns: string
  date: string
}

type BenchmarkToggle = {
  name: string
  type: string
  value: string
  toggleValue: string
  toggleCaption: string
}

type FundToggle = Omit<BenchmarkToggle, 'type' | 'value'>

type TenureTab = {
  tenure: number
  label: string
  period: `${TIME_UNITS}`
}

type TenureTabs = TenureTab[]

const TRAILING_PERFORMANCE_SCREEN_NAME = `MF_L2_Trailing_Returns_Performance`
function sanitizeData(
  currFunds: TrailingReturnsPerformanceData['currFunds'],
  benchmarkFunds: TrailingReturnsPerformanceData['benchmarkFunds']
) {
  // Step 1: Sort currFunds based on x_value
  const sortedCurrFunds = currFunds?.sort(
    (a: { x_value: number }, b: { x_value: number }) => (a?.x_value ?? 0) - (b?.x_value ?? 0)
  )

  // Step 2: Create new arrays
  const newCurrFunds: TrailingReturnsPerformanceData['currFunds'] = []
  const newBenchmarkFunds: TrailingReturnsPerformanceData['benchmarkFunds'] = []

  // Step 3: Create a map of benchmarkFunds for quick lookup
  const benchmarkMap = new Map(benchmarkFunds.map((fund: { x_value: any }) => [fund.x_value, fund]))

  // Step 4: Iterate through sorted currFunds
  sortedCurrFunds.forEach((currFund: { x_value: any }) => {
    const x_value = currFund.x_value
    const benchmarkFund = benchmarkMap.get(x_value)

    // Step 5: Populate new arrays if benchmarkFund exists
    if (benchmarkFund) {
      newCurrFunds.push(currFund as TrailingReturnsPerformanceData['currFunds'][0])
      newBenchmarkFunds.push(benchmarkFund as TrailingReturnsPerformanceData['benchmarkFunds'][0])
    }
    // If benchmarkFund does not exist, do not add to new arrays
  })
  return {
    newCurrFunds,
    newBenchmarkFunds
  }
}

export default function TrailingReturnsPerformanceSection() {
  const { isin, data: metadata } = useMetaDataContext()
  const [trailingData, setTrailingData] = useState<TrailingReturnsPerformanceData>()
  const [currentFundtoggle, setCurrentFundToggle] = useState<FundToggle>({
    name: 'This fund',
    toggleValue: '',
    toggleCaption: ''
  })
  const [currentBenchmarktoggle, setCurrentBenchmarkToggle] = useState<BenchmarkToggle>({
    name: metadata?.benchmarkFunds?.[0]?.name ?? '',
    type: metadata?.benchmarkFunds?.[0]?.type ?? '',
    value: metadata?.benchmarkFunds?.[0]?.value ?? '',
    toggleValue: '',
    toggleCaption: ''
  })
  const [selectedTenure, setSelectedTenure] = useState<TenureTab>({
    tenure: metadata?.trailingReturns?.tenure?.value ?? 0,
    label: convertUnitAndValue({
      value: metadata?.trailingReturns?.tenure?.value ?? 0,
      unit: metadata?.trailingReturns?.tenure?.period ?? UNITS.MONTH,
      format: {
        casing: 'PASCAL',
        type: 'SHORT'
      }
    }),
    period: metadata?.trailingReturns?.tenure?.period ?? UNITS.MONTH
  })
  const [tenureTabs, setTenureTabs] = useState<TenureTabs>([])
  const [returnsDate, setReturnsDate] = useState<string>(
    `${convertUnitAndValue({
      value: metadata?.trailingReturns?.tenure?.value ?? 0,
      unit: metadata?.trailingReturns?.tenure?.period ?? UNITS.MONTH,
      format: {
        casing: 'PASCAL',
        type: 'SHORT'
      }
    })} Returns`
  )
  const [openBottomSheet, setOpenBottomSheet] = useState(false)
  const [bottomSheetContentKey, setBottomSheetContentKey] = useState('')

  const isInteractionEventLocked = useRef<boolean>(false)

  useEffect(() => {
    if (selectedTenure)
      setReturnsDate(
        () =>
          `${selectedTenure?.label} ${selectedTenure?.tenure > 1 && selectedTenure?.period === UNITS.YEAR ? 'Annualised' : 'Trailing'} Returns`
      )
  }, [selectedTenure])

  const { data, isLoading, isFetching, isError } = useQuery<any, Error>({
    queryKey: [
      'trailingReturnsPerformanceData',
      isin,
      currentBenchmarktoggle?.type,
      currentBenchmarktoggle?.value,
      selectedTenure?.tenure,
      selectedTenure?.period,
      metadata?.trailingReturns?.principal
    ],
    queryFn: () => {
      return fetchTrailingReturnsPerformanceData({
        isin,
        benchmarkType: currentBenchmarktoggle?.type,
        benchmarkValue: currentBenchmarktoggle?.value,
        tenureValue: selectedTenure?.tenure,
        tenurePeriod: selectedTenure?.period,
        principal: metadata?.trailingReturns?.principal
      })
    },
    retry: 1,
    refetchOnWindowFocus: false
  })

  useEffect(() => {
    if (data) {
      const dataCopy: TrailingReturnsPerformanceData = data?.data
      const { newCurrFunds, newBenchmarkFunds } = sanitizeData(dataCopy.currFunds, dataCopy.benchmarkFunds)
      setTrailingData((prevData) => ({
        ...prevData,
        ...dataCopy,
        currFunds: newCurrFunds,
        benchmarkFunds: newBenchmarkFunds
      }))
      const tenures = dataCopy?.toggleValues ?? []
      const sortedTenures = sortTenures(tenures)
      const modifiedTenures: TenureTab[] = sortedTenures?.map((tenure: { value: any; period: any }) => {
        return {
          tenure: tenure?.value,
          label: convertUnitAndValue({
            value: tenure?.value,
            unit: tenure?.period,
            format: {
              casing: 'PASCAL',
              type: 'SHORT'
            }
          }),
          period: tenure?.period
        }
      })
      if (!metadata?.trailingReturns?.tenure && tenureTabs?.length === 0) setSelectedTenure(modifiedTenures?.[0])
      setTenureTabs(() => modifiedTenures as TenureTabs)
      const lastBenchmarkFundPoint = newBenchmarkFunds?.slice(-1)?.[0]
      const lastCurrFundPoint = newCurrFunds?.slice(-1)?.[0]
      setCurrentBenchmarkToggle((currentBenchmarktogglePrev) => {
        return {
          ...currentBenchmarktogglePrev,
          toggleValue: convertUnitAndValue({
            value: +(lastBenchmarkFundPoint?.label ?? '0'),
            unit: UNITS.PERCENTAGE
          }),
          toggleCaption: `${roundOffAndFormatNumberWithCurrency(+(lastBenchmarkFundPoint?.y_value ?? '0'))}`
        }
      })
      setCurrentFundToggle(() => {
        return {
          name: 'This Fund',
          toggleValue: convertUnitAndValue({
            value: +(lastCurrFundPoint?.label ?? '0'),
            unit: UNITS.PERCENTAGE
          }),
          toggleCaption: `${roundOffAndFormatNumberWithCurrency(+(lastCurrFundPoint?.y_value ?? '0'))}`
        }
      })
    }
  }, [data])

  const handleGraphInteraction = (thisFund: FundReturnType, benchmarkFund: FundReturnType, isLastPoint: boolean) => {
    if (!isInteractionEventLocked.current) {
      trackEvent({
        eventName: EventNames.GraphInteraction,
        payload: {
          eventId: 'slider_move',
          responseName: `${selectedTenure?.label}`,
          ScreenName: TRAILING_PERFORMANCE_SCREEN_NAME,
          fundName: isin,
          screenType: 'MF_L2'
        }
      })
      isInteractionEventLocked.current = true
    }
    setReturnsDate(() => {
      if (isLastPoint)
        return `${selectedTenure?.label} ${selectedTenure?.tenure > 1 && selectedTenure?.period === UNITS.YEAR ? 'Annualised' : 'Trailing'} Returns`
      return `Returns on ${formatDate(thisFund?.date)}`
    })
    setCurrentFundToggle((currentFundtogglePrev) => {
      return {
        ...currentFundtogglePrev,
        toggleValue: thisFund.returns
          ? convertUnitAndValue({
              value: +(thisFund.returns ?? '0'),
              unit: UNITS.PERCENTAGE
            })
          : currentFundtogglePrev?.toggleValue,
        toggleCaption: thisFund.amount
          ? `${roundOffAndFormatNumberWithCurrency(+(thisFund.amount ?? '0'))}`
          : currentFundtogglePrev?.toggleCaption
      }
    })
    setCurrentBenchmarkToggle((currentBenchmarktogglePrev) => {
      return {
        ...currentBenchmarktogglePrev,
        toggleValue: benchmarkFund.returns
          ? convertUnitAndValue({
              value: +(benchmarkFund.returns ?? '0'),
              unit: UNITS.PERCENTAGE
            })
          : currentBenchmarktogglePrev?.toggleValue,
        toggleCaption: benchmarkFund.amount
          ? `${roundOffAndFormatNumberWithCurrency(+(benchmarkFund.amount ?? '0'))}`
          : currentBenchmarktogglePrev?.toggleCaption
      }
    })
  }

  useEffect(() => {
    trackEvent({
      eventName: EventNames.ScreenLoaded,
      payload: {
        ScreenName: TRAILING_PERFORMANCE_SCREEN_NAME,
        fundName: isin,
        screenType: 'MF_L2'
      }
    })
  }, [])

  const handleTenureTabClick = (tabId: any) => {
    const filterTab = tenureTabs?.filter((tab) => tab?.label === tabId)?.[0]
    trackEvent({
      eventName: EventNames.GraphInteraction,
      payload: {
        eventId: 'graph_period',
        responseName: filterTab?.label ?? '',
        ScreenName: TRAILING_PERFORMANCE_SCREEN_NAME,
        fundName: isin,
        screenType: 'MF_L2'
      }
    })
    setSelectedTenure(() => {
      return {
        label: tabId,
        tenure: filterTab?.tenure,
        period: filterTab?.period
      }
    })
  }

  const handleToggleClick = (name: string) => {
    const filteredBenchmark = metadata?.benchmarkFunds?.filter((benchmark) => benchmark?.name === name)?.[0]
    trackEvent({
      eventName: EventNames.GraphInteraction,
      payload: {
        eventId: 'benchmarks',
        responseName: filteredBenchmark?.name ?? '',
        ScreenName: TRAILING_PERFORMANCE_SCREEN_NAME,
        fundName: isin,
        screenType: 'MF_L2'
      }
    })
    setCurrentBenchmarkToggle({
      name: name,
      type: filteredBenchmark?.type ?? '',
      value: filteredBenchmark?.value ?? '',
      toggleValue: '',
      toggleCaption: ''
    })
  }

  const renderBottomSheetContent = () => {
    switch (bottomSheetContentKey) {
      case 'trContent':
        return (
          <ReturnsBottomSheet
            onDismiss={() => setOpenBottomSheet((open) => !open)}
            ScreenName={TRAILING_PERFORMANCE_SCREEN_NAME}
          />
        )
      case 'learnMore':
        return (
          <TRLearnMoreBottomSheet
            onDismiss={() => setOpenBottomSheet((open) => !open)}
            ScreenName={TRAILING_PERFORMANCE_SCREEN_NAME}
          />
        )
      default:
        return null
    }
  }

  if (isLoading || isFetching)
    return (
      <div className='w-full px-2 h-max flex flex-col gap-2'>
        <TrailingGraphLoader />
      </div>
    )

  return (
    <div className='bg-white rounded-2 flex flex-col pb-[24px] w-full'>
      {metadata?.distributionStatus === 'IDCW' && (
        <div className='pb-6 w-full '>
          <Remark>
            <>
              The below returns might be lower as it{' '}
              <span className='text-[#004D99] font-[500]'> does not includes returns from payouts </span>
            </>
          </Remark>
        </div>
      )}
      <div className='flex items-center justify-between pb-3 flex-row'>
        <div>
          <h3 className='font-[500] leading-[150%] m-0 text-[14px]'>{`${returnsDate}`} </h3>
          <div className='text-13 text-[#6A6E6E]'>
            For investment of {`${roundOffAndFormatNumberWithCurrency(metadata?.trailingReturns?.principal ?? 10000)}`}
            {''}
          </div>
        </div>
        <HelpButton
          aria-label='Help about trailing returns'
          onClick={() => {
            setBottomSheetContentKey('trContent')
            setOpenBottomSheet((open) => !open)
          }}
        />
      </div>

      <div className='w-full flex gap-3'>
        <div className='flex ' style={{ width: 'calc(50% - 6px)' }}>
          <ToggleBox
            isToggleEnabled={false}
            toggleBoxClassName='bg-[#E6F2FF]'
            titleClassName='text-[#004D99]'
            valueClassName='text-[#004D99]'
            captionClassName='text-[#003D7A]'
            toggleOptions={['This fund']}
            currentToggleOption='This fund'
            onToggleClick={() => null}
            value={currentFundtoggle?.toggleValue}
            caption={currentFundtoggle?.toggleCaption}
          />
        </div>
        <div className='flex ' style={{ width: 'calc(50% - 6px)' }}>
          <ToggleBox
            isToggleEnabled={true}
            toggleBoxClassName='bg-[#FAEFE3]'
            titleClassName='text-[#992E00]'
            valueClassName='text-[#992E00]'
            captionClassName='text-[#7A2400]'
            toggleOptions={metadata?.benchmarkFunds?.map((benchmark) => benchmark?.name) ?? []}
            currentToggleOption={currentBenchmarktoggle?.name}
            onToggleClick={handleToggleClick}
            value={currentBenchmarktoggle?.toggleValue}
            caption={currentBenchmarktoggle?.toggleCaption}
            toggleColor='#992E00'
          />{' '}
        </div>
      </div>
      {isError ? (
        <div className='flex flex-col w-full py-2'>
          <div className='w-full h-[240px] py-5'>
            <GraphUnavailable />
          </div>
          <div className='text-13 text-[#6A6E6E]'>
            Fund is newly launched. Graph will be available as soon as we have enough data.
          </div>
        </div>
      ) : (
        <div className='h-[275px] w-full'>
          {trailingData && (
            <TrailingReturnsGraph
              tenureLabel={selectedTenure?.label}
              actualData={trailingData}
              onUpdateCurrentValue={handleGraphInteraction}
            />
          )}
        </div>
      )}
      {tenureTabs?.length > 0 && (
        <div className='pt-4 flex gap-2'>
          <div className='flex-1 h-8 w-0'>
            <Tabs
              isFullWidth
              tabsData={tenureTabs?.map((tenureTab) => {
                return {
                  id: tenureTab?.label,
                  label: tenureTab?.label
                }
              })}
              selectedTabId={selectedTenure?.label}
              onTabClick={handleTenureTabClick}
            />
          </div>
          {/* <div className='w-[38px]'>
            <Button onClick={() => null} buttonClassName='min-w-[20px] w-[38px] min-h-9'>
              <img src={`${MUTUAL_FUND_DETAIL_IMAGE_URL}/help-icon.svg`} />
            </Button>
          </div> */}
        </div>
      )}
      <div className='pt-6'>
        <TextBubble
          text='This metric only compares point to point returns, and may not capture how consistent the fund is over time.'
          learnMoreText='See why'
          onLearnMore={() => {
            lightImpactVibrate()
            setBottomSheetContentKey('learnMore')
            setOpenBottomSheet((open) => !open)
          }}
        />
      </div>
      <VaulDrawer open={openBottomSheet} setOpen={setOpenBottomSheet}>
        {renderBottomSheetContent()}
      </VaulDrawer>
    </div>
  )
}
